import { MeasureEntity, View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";

export type PointSnapInfo = {
    connection: ReadonlyVec3;
} & (
    | {
          type: "vertex";
      }
    | {
          type: "corner";
          guides: ReadonlyVec3[]; // not normals, actual points in 3D space
          /**
           * The adjacent vertices `guides` point at, at their true positions. `guides` are pulled in
           * to unit distance from `connection` for drawing, which loses the actual geometry.
           */
          adjacentVertices: ReadonlyVec3[];
      }
    | {
          type: "tangent";
          start: ReadonlyVec3;
          end: ReadonlyVec3;
      }
    | {
          type: "midpoint";
          start: ReadonlyVec3;
          end: ReadonlyVec3;
      }
    | {
          type: "normal";
          normal: ReadonlyVec3;
      }
);

export type PointSnapInfo2d = {
    connection: ReadonlyVec2;
} & (
    | {
          type: "vertex";
      }
    | {
          type: "corner";
          guides: (ReadonlyVec2 | undefined)[];
      }
    | {
          type: "tangent";
          start: ReadonlyVec2 | undefined;
          end: ReadonlyVec2 | undefined;
      }
    | {
          type: "midpoint";
          start: ReadonlyVec2 | undefined;
          end: ReadonlyVec2 | undefined;
      }
    | {
          type: "normal";
          normal: ReadonlyVec2 | undefined;
      }
);

export async function getSnapInfo({
    view,
    entity,
    connectionPoint,
    corners = false,
}: {
    view: View;
    entity: MeasureEntity;
    connectionPoint: ReadonlyVec3;
    corners?: boolean;
}): Promise<PointSnapInfo | undefined> {
    if (entity.drawKind === "vertex" && corners) {
        const guides: ReadonlyVec3[] = [];
        const adjacentVertices: ReadonlyVec3[] = [];
        const edges = await view.measure?.core.getVertexEdges(entity);
        if (edges?.length) {
            for (const edge of edges) {
                if (edge.kind === "line") {
                    // TODO for some reason irrelevant points may be returned, check getVertexEdges
                    const isStartVertex = vec3.dist(edge.start, connectionPoint) < 1e-6;
                    const isEndVertex = vec3.dist(edge.end, connectionPoint) < 1e-6;
                    if (!isStartVertex && !isEndVertex) {
                        continue;
                    }
                    const point = isStartVertex ? edge.end : edge.start;
                    adjacentVertices.push(vec3.clone(point));
                    const guide = vec3.sub(vec3.create(), point, connectionPoint);
                    vec3.normalize(guide, guide);
                    vec3.add(guide, guide, connectionPoint);
                    guides.push(guide);
                }
            }
        }
        return { connection: connectionPoint, type: "corner", guides, adjacentVertices };
    } else if (entity.drawKind === "vertex") {
        return {
            connection: connectionPoint,
            type: "vertex",
        };
    } else if (entity.tangent) {
        // Check to see if the snap is the middle of the tangent
        const start = vec3.add(vec3.create(), connectionPoint, entity.tangent);
        const end = vec3.scaleAndAdd(vec3.create(), connectionPoint, entity.tangent, -1);

        return {
            connection: connectionPoint,
            type: entity.curvePart === "edgeMidpoint" ? "midpoint" : "tangent",
            start,
            end,
        };
    } else if (entity.normal) {
        return {
            connection: connectionPoint,
            type: "normal",
            normal: entity.normal,
        };
    } else {
        return {
            connection: connectionPoint,
            type: "vertex",
        };
    }
}

export function getSnapInfo2d(
    view: View,
    toScreenSpace: (p: ReadonlyVec3) => vec2 | undefined,
    snapInfo: PointSnapInfo,
): PointSnapInfo2d | undefined {
    const connection2d = toScreenSpace(snapInfo.connection);
    const guides2d: (ReadonlyVec2 | undefined)[] = [];

    if (!connection2d) {
        return;
    }

    if (snapInfo.type === "corner") {
        snapInfo.guides.forEach((guide, index) => {
            const guide2d = toScreenSpace(guide);
            if (!guide2d) {
                guides2d.push(undefined);
                return;
            }

            vec2.sub(guide2d, guide2d, connection2d);
            vec2.normalize(guide2d, guide2d);

            guides2d[index] = guide2d;
        });

        return { connection: connection2d, type: "corner", guides: guides2d };
    } else if (snapInfo.type === "tangent") {
        return {
            connection: connection2d,
            type: "tangent",
            start: toScreenSpace(snapInfo.start),
            end: toScreenSpace(snapInfo.end),
        };
    } else if (snapInfo.type === "midpoint") {
        return {
            connection: connection2d,
            type: "midpoint",
            start: toScreenSpace(snapInfo.start),
            end: toScreenSpace(snapInfo.end),
        };
    } else if (snapInfo.type === "normal") {
        const point = vec3.add(vec3.create(), snapInfo.connection, snapInfo.normal);
        const normal = toScreenSpace(point);
        if (normal) {
            vec2.sub(normal, normal, connection2d);
        }

        return {
            connection: connection2d,
            type: "normal",
            normal,
        };
    } else {
        return {
            connection: connection2d,
            type: "vertex",
        };
    }
}

export function getSnapGuides(snap: PointSnapInfo) {
    if (snap.type === "corner") {
        return snap.guides;
    } else if (snap.type === "tangent" || snap.type === "midpoint") {
        const guides: ReadonlyVec3[] = [];
        if (snap.start) {
            guides.push(snap.start);
        }
        if (snap.end) {
            guides.push(snap.end);
        }
        return guides;
    }
    return [];
}

/**
 * Resolves the world-space positions of the vertices adjacent to a snapped feature:
 * - `corner`: the far endpoints of the straight edges meeting at the corner vertex, already
 *   resolved by {@link getSnapInfo}.
 * - `tangent` / `midpoint`: the two endpoints of the snapped (straight) edge, which the snap info
 *   does not carry (its `start`/`end` are unit tangent offsets, not the real endpoints).
 *
 * Returns `undefined` for snap types without adjacent vertices (`vertex`, `normal`), and an empty
 * array when the adjacent geometry is not a straight edge (e.g. arcs).
 */
export async function getSnapAdjacentVertices(
    view: View,
    entity: MeasureEntity,
    snap: PointSnapInfo,
): Promise<ReadonlyVec3[] | undefined> {
    if (snap.type === "corner") {
        return snap.adjacentVertices;
    }

    if (snap.type === "tangent" || snap.type === "midpoint") {
        if (!view.measure) {
            return undefined;
        }
        // Measuring a single edge entity yields its LineValues (with the two endpoints) for straight
        // edges; arcs/other kinds have no simple endpoint pair, so we return an empty array.
        const values = await view.measure.core.measure(entity);
        return values && "kind" in values && values.kind === "line" ? [values.start, values.end] : [];
    }

    return undefined;
}

export function getSnapGuides2d(snap: PointSnapInfo2d) {
    if (snap.type === "corner") {
        return snap.guides.filter(Boolean) as ReadonlyVec2[];
    } else if (snap.type === "tangent" && snap.start && snap.end) {
        const dir = vec2.sub(vec2.create(), snap.end, snap.start);
        vec2.normalize(dir, dir);
        return [dir, vec2.negate(vec2.create(), dir)];
    } else if (snap.type === "midpoint" && snap.start && snap.end) {
        const dir = vec2.sub(vec2.create(), snap.end, snap.start);
        vec2.normalize(dir, dir);
        vec2.rotate(dir, dir, [0, 0], Math.PI / 2);
        return [dir, vec2.negate(vec2.create(), dir)];
    }
    return [];
}
